import SyntaxAnalysisSelection from "./SyntaxAnalysisSelection";
import SentenceTestSelection from "./SentenceTestSelection";
import ReadingSelection from "./ReadingSelection";
import DirectTranslationSelection from "./DirectTranslationSelection";
import FlowSelection from "./FlowSelection";

/**
 * ── 변경사항 (ECHO 톤) ──
 * 1. 체크박스 가로 나열 → 설명이 붙은 카드 그리드 (ws-option-card)
 * 2. 각 유형에 설명 추가 — 선생님이 뭘 출제하는지 바로 알 수 있게
 * 3. 하위 설정(난이도/문장학습 등)은 선택 시 아래에 섹션으로 등장 (기존과 동일)
 * 4. handleTypeClick / selectedTypes 데이터 구조는 기존 그대로
 */

const TYPE_DESCRIPTIONS = {
  keypoints: "지문의 핵심 문장을 빈칸으로 정리하는 문제",
  reading: "지문을 문장 단위로 읽으며 이해하는 문제",
  direct_translation: "끊어읽기 단위로 듣고 따라 읽으며 해석하는 문제",
  words: "지문 속 단어의 뜻과 철자를 학습하는 문제",
  syntax_analysis: "문장 성분(주어·동사·목적어·보어)을 구분하는 문제",
  flow: "문장 순서를 배열하며 글의 흐름을 파악하는 문제",
  grammar: "지문 문장으로 만든 문법 문제. 문법 문항이 있는 지문에만 나갑니다",
  sentence_test: "해석·빈칸·스크램블로 문장을 반복 연습하는 문제",
};

const AnalysisSelectionPage = ({
  allTypes,
  selectedTypes,
  setSelectedTypes,
}) => {
  const isReadOnly = setSelectedTypes === undefined;

  const handleTypeClick = (type) => {
    setSelectedTypes((prev) => {
      if (prev.some((item) => item.key === type.key)) {
        return prev.filter((item) => item.key !== type.key);
      }
      if (type.key === "syntax_analysis") {
        return [
          ...prev,
          { key: type.key, value: type.value, level: { key: "", value: "" } },
        ];
      } else if (type.key === "direct_translation") {
        return [
          ...prev,
          {
            key: type.key,
            value: type.value,
            options: [],
            speech_rate: 1,
            // 통과 기준. 받아쓰기가 관사나 복수형을 자주 흘려서 100 을 요구하면
            // 잘 읽은 아이가 계속 막힌다. 그래서 기본을 70 으로 둔다.
            fluency_passing_score: 70,
            interpretation_passing_score: 70,
            // 유창성·통역이 기본으로 켜지므로 읽기 채점도 함께 켠다. 꺼 두면
            // 아이가 소리 내어 읽어도 아무 일이 없이 다음으로 넘어간다.
            is_pronunciation_check_enabled: true,
          },
        ];
      } else if (type.key === "sentence_test") {
        return [...prev, { key: type.key, value: type.value, options: [] }];
      } else if (type.key === "flow") {
        return [
          ...prev,
          { key: type.key, value: type.value, kor: true, eng: true },
        ];
      }
      return [...prev, { key: type.key, value: type.value }];
    });
  };

  return (
    <div style={{ padding: "22px" }}>
      <div className="ws-modal-title">출제 유형 선택</div>
      <p className="ws-modal-sub" style={{ marginBottom: "16px" }}>
        여러 유형을 조합할 수 있어요. 카드를 눌러 선택하세요.
      </p>

      {/* 카드 그리드 */}
      <div className="ws-option-grid">
        {allTypes.map((type) => {
          const isSelected = selectedTypes.some(
            (item) => item.key === type.key
          );
          return (
            <div
              key={type.key}
              className={`ws-option-card${isSelected ? " selected" : ""}`}
              onClick={() => !isReadOnly && handleTypeClick(type)}
              style={{ cursor: isReadOnly ? "default" : "pointer" }}
            >
              <div className="card-check" />
              <div className="card-name">{type.value}</div>
              <div className="card-desc">{TYPE_DESCRIPTIONS[type.key]}</div>
            </div>
          );
        })}
      </div>

      {/* 하위 설정 */}
      {selectedTypes.some((item) => item.key === "flow") && (
        <SubSection title="흐름정리 언어">
          <FlowSelection
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
          />
        </SubSection>
      )}

      {selectedTypes.some((item) => item.key === "reading") && (
        <SubSection title="지문읽기 설정">
          <ReadingSelection
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
          />
        </SubSection>
      )}

      {selectedTypes.some((item) => item.key === "direct_translation") && (
        <SubSection title="직독직해 단계">
          <DirectTranslationSelection
            allTypes={allTypes}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
          />
        </SubSection>
      )}

      {selectedTypes.some((item) => item.key === "syntax_analysis") && (
        <SubSection title="구문분석 난이도">
          <SyntaxAnalysisSelection
            allTypes={allTypes}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
          />
        </SubSection>
      )}

      {selectedTypes.some((item) => item.key === "sentence_test") && (
        <SubSection title="문장학습 세부 설정">
          <SentenceTestSelection
            allTypes={allTypes}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
          />
        </SubSection>
      )}
    </div>
  );
};

const SubSection = ({ title, children }) => (
  <div
    style={{
      marginTop: "20px",
      paddingTop: "16px",
      borderTop: "1px solid #ECF1F2",
    }}
  >
    <div
      style={{
        fontSize: "0.78rem",
        fontWeight: 800,
        letterSpacing: "0.05em",
        color: "#00A396",
        marginBottom: "10px",
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

export default AnalysisSelectionPage;
